import { IconButton, Layout, LoadingDots, Tag, Text } from '@loadsmart/loadsmart-ui'
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { Delete } from '@mui/icons-material'
import ContentTypeTag from 'atoms/ContentTypeTag/ContentTypeTag'
import DirectionTag from 'atoms/DirectionTag/DirectionTag'
import LoaderTag from 'atoms/LoaderTag/LoaderTag'
import LSDate, { DATE_FORMAT_MMDDYYYYHHMM } from 'common/Date.helpers'
import { TranslationMap } from 'common/types/kraken-core/TranslationMap'
import isEmpty from 'lodash.isempty'
import { useHistory } from 'react-router'
import PushableEntityStatusTag from '../../../common/components/development/PusableEntityStatus'

export interface TranslationMapsListTableProps {
  data: Array<TranslationMap>
  isLoading: boolean
  onDetail?: () => void
  onRemove?: (entity: TranslationMap) => void
}

export default function TranslationMapsListTable(props: TranslationMapsListTableProps) {
  const history = useHistory()
  const COLUMNS = [
    {
      Header: '#',
      style: {
        width: '100px',
        minWidth: '100px',
        maxWidth: '100px',
      },
      Cell(row: TranslationMap) {
        const idAsString = row.id?.slice(0, 8) || '-'
        return (
          <Tag
            className="cursor-pointer"
            variant="default"
            onClick={() => {
              history.push(`/translation-maps/${row.id}`)
            }}
          >
            {idAsString}
          </Tag>
        )
      },
    },
    {
      Header: 'Name',
      style: {
        minWidth: '200px',
      },
      Cell(row: TranslationMap) {
        return row.name
      },
    },
    {
      Header: 'Input',
      style: {
        width: '150px',
        minWidth: '150px',
        maxWidth: '150px',
      },
      Cell(row: TranslationMap) {
        return row.input_content_type ? (
          <ContentTypeTag contentType={row.input_content_type} size="small" />
        ) : (
          '-'
        )
      },
    },
    {
      Header: 'Output',
      style: {
        width: '150px',
        minWidth: '150px',
        maxWidth: '150px',
      },
      Cell(row: TranslationMap) {
        return row.output_content_type ? (
          <ContentTypeTag contentType={row.output_content_type} size="small" />
        ) : (
          '-'
        )
      },
    },
    {
      Header: 'Loader',
      style: {
        width: '150px',
        minWidth: '150px',
        maxWidth: '150px',
      },
      Cell(row: TranslationMap) {
        return <LoaderTag loader={row.loader} />
      },
    },
    {
      Header: 'Direction',
      style: {
        width: '100px',
        minWidth: '100px',
        maxWidth: '100px',
      },
      Cell(row: TranslationMap) {
        return <DirectionTag direction={row.use_when_direction} />
      },
    },
    {
      Header: 'Status',
      style: {
        width: '140px',
        minWidth: '140px',
        maxWidth: '140px',
      },
      Cell(row: TranslationMap) {
        return row.status ? <PushableEntityStatusTag status={row.status} size="default" /> : null
      },
    },
    {
      Header: 'Updated at',
      style: {
        width: '140px',
        minWidth: '140px',
        maxWidth: '140px',
      },
      Cell(row: TranslationMap) {
        return LSDate(row.updated_at)?.format(DATE_FORMAT_MMDDYYYYHHMM) || '-'
      },
    },
    {
      Header: 'Actions',
      style: {
        width: '120px',
        minWidth: '120px',
        maxWidth: '120px',
      },
      Cell(row: TranslationMap) {
        return (
          <Layout.Group justify="flex-end" space="xs">
            <IconButton
              onClick={() => (props.onRemove ? props.onRemove(row) : null)}
              title="Delete"
            >
              <Delete sx={{ fontSize: 16 }} />
            </IconButton>
          </Layout.Group>
        )
      },
    },
  ]

  return (
    <>
      <Table size="small" style={{ overflow: 'auto' }}>
        <TableHead>
          <TableRow>
            {COLUMNS.map((c, index) => (
              <TableCell style={c.style} key={`header-${index}`}>
                <Text variant="button-sm">{c.Header}</Text>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody key={'table-body'}>
          {props.data.length > 0 &&
            props.data.map((row, index) => {
              return (
                <TableRow key={`table-row-${row.id}`}>
                  {COLUMNS.map((column, columnIndex) => (
                    <TableCell
                      style={column.style}
                      width={column.style.width}
                      size="small"
                      key={`table-cell-${index}-${columnIndex}`}
                    >
                      {column.Cell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}

          {props.data.length <= 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="left">
                {isEmpty(props.data) && !props.isLoading ? (
                  <Layout.Stack className="p-2" space="l">
                    <Text variant="body-bold">No translation maps available.</Text>
                  </Layout.Stack>
                ) : props.isLoading ? (
                  <LoadingDots />
                ) : null}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </>
  )
}
